import type { ReactNode } from 'react';
import { cn } from './cn.js';

/**
 * Selo de situação.
 *
 * Existe porque a terceira tela precisou dele e porque a alternativa em uso era
 * `<span className="text-sucesso">✓ Ativo</span>`: glifo de texto fazendo as
 * vezes de ícone, e cor carregando sozinha o significado em outros lugares.
 *
 * A regra que este componente torna impossível de quebrar: **estado é cor +
 * símbolo + texto**, nunca dois dos três. Quem não distingue vermelho de verde
 * lê o símbolo; quem usa leitor de tela lê o texto.
 *
 * O verde é estado, jamais ação — por isso não existe `Badge` clicável. Se
 * precisa de clique, é `Button`.
 */
import type { Tone } from '@evogard/domain';

/*
 * As chaves são os VALORES de `Tone`, não nomes nossos — o mapa é indexado
 * pela união literal, e traduzi-las quebra a indexação.
 */
// @valor
const tones: Record<Tone, string> = {
  sucesso: 'bg-sucesso-suave text-sucesso',
  erro: 'bg-erro-suave text-erro',
  alerta: 'bg-alerta-suave text-alerta',
  informacao: 'bg-primaria-suave text-primaria-texto',
  neutro: 'bg-superficie-suave text-texto-suave',
  progresso: 'bg-progresso-suave text-progresso',
};

export function Badge({
  tone = 'neutro',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pilula px-2.25 py-1 text-[0.71875rem] font-bold',
        tones[tone],
        className,
      )}
    >
      {/*
       * Um PONTO, e não o ícone do tom.
       *
       * O desenho de 05/09 desenha a pílula assim, e a troca é segura porque o
       * `children` desta peça é obrigatório: o que carrega o estado é a
       * palavra — "Concluída", "Vencida" —, e a cor só a reforça. A WCAG 1.4.1
       * pede que a cor não seja o ÚNICO meio, e o texto já é o outro.
       *
       * O que se perde é a varredura rápida de quem não distingue as cores: um
       * visto e um triângulo se separam de longe, dois pontos não. Por isso o
       * ponto herda `currentColor` do tom e nunca é cinza — quem enxerga a cor
       * ganha a pista, quem não enxerga lê a palavra, e ninguém depende só dela.
       */}
      <span
        aria-hidden
        className="size-1.25 shrink-0 rounded-pilula bg-current"
      />
      {children}
    </span>
  );
}
